import { CanMatchFn, Router, UrlTree } from '@angular/router';
import { CONVERSATION_QUERY_PARAM_NAMES, QUERY_PARAMS, ROOT_ROUTES, TDataQueryName } from '../configurations/router';
import { inject } from '@angular/core';
import { EErrorId } from '../../shared/components/not-found/not-found.component';
import { TranscriptionService } from '../services/transcription.service';
import { HttpErrorResponse } from '@angular/common/http';
import { EHttpErrorStatus } from '../configurations/errors';
import { Store } from '@ngrx/store';
import { APPLICATION_ACTIONS, IDataQueryParam } from '../../store/actions';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { APPLICATION_REDUCER } from '../../store/reducers/application.reducer';

const getNotFoundUrlTree = (router: Router, errorId: EErrorId): UrlTree =>
  router.createUrlTree([ROOT_ROUTES.notFound], {
    queryParams: {
      [QUERY_PARAMS.errorId]: errorId,
    },
  });

const getUpdatedQueryUrlTree = (router: Router, queryName: string, ids: string[]): UrlTree =>
  router.createUrlTree([], {
    queryParams: { [queryName]: ids.join(',') },
    queryParamsHandling: 'merge',
  });

const onValidationError = (
  response: HttpErrorResponse,
  router: Router,
  queryName: TDataQueryName,
  availableIds: string[],
): UrlTree => {
  if (availableIds.length) {
    return getUpdatedQueryUrlTree(router, queryName, availableIds);
  } else if ((response.status as EHttpErrorStatus) === EHttpErrorStatus.serverIssue) {
    return getNotFoundUrlTree(router, EErrorId.serverIssue);
  } else {
    return getNotFoundUrlTree(router, EErrorId.missedTranscription);
  }
};

const validateDataId = (
  store: Store,
  service: TranscriptionService,
  queryName: TDataQueryName,
  currentId: string,
): Observable<void> =>
  service.validateId(queryName, String(currentId)).pipe(
    tap({
      next: () => store.dispatch(APPLICATION_ACTIONS.setDataQueryParam({ queryName, value: currentId })),
      subscribe: () => store.dispatch(APPLICATION_ACTIONS.showLoader()),
      finalize: () => store.dispatch(APPLICATION_ACTIONS.hideLoader()),
    }),
  );

export const transcriptionIdValidationGuard: CanMatchFn = async (): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const store = inject(Store);
  const transcriptionService = inject(TranscriptionService);
  const { queryParams = {} } = router.getCurrentNavigation()?.extractedUrl ?? {};
  const storedDataQueryParam: IDataQueryParam = await firstValueFrom(
    store.select(APPLICATION_REDUCER.selectDataQueryParam),
  );

  for (const queryName of Object.keys(queryParams) as TDataQueryName[]) {
    if (!CONVERSATION_QUERY_PARAM_NAMES.includes(queryName)) continue;
    const dataIds: string[] = (queryParams[queryName] as string).split(',');
    const activeDataId: string = dataIds[0];

    if (storedDataQueryParam.queryName === queryName && storedDataQueryParam.value === activeDataId) {
      return true;
    }

    try {
      await firstValueFrom(validateDataId(store as Store, transcriptionService, queryName, activeDataId));
      return true;
    } catch (response) {
      return onValidationError(response as HttpErrorResponse, router, queryName, dataIds.slice(1));
    }
  }

  return getNotFoundUrlTree(router, EErrorId.missedTranscriptionId);
};
